import React, { useState, useRef } from 'react';
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { PiIdentificationCardFill } from 'react-icons/pi';
import { FaSquarePhone } from 'react-icons/fa6';
import Modal from "react-bootstrap/Modal";
import ReCaptcha from "react-google-recaptcha-enterprise";

import back from "~/assets/images/arrow.svg";
import valegasicon from '~/assets/images/vale-gas.svg';
import { InfoButton, InfoButtonDisabled } from '~/components/layout/Buttons';
import HeaderDesktop from "~/components/layout/Header";
import SideBar from "~/components/layout/Sidebar";
import api from '~/services/api';
import HeaderMobile from '~/components/layout/HeaderOld';
import OpenChatBot from '~/components/layout/Chatbot';
import { isValidCPF } from '~/utils/cpf';
import { isValidPhone, formatPhone } from '~/utils/phone';
import { requireCpfPhoneValegas } from '~/config/valegas';

import {
  BackIcon,
  Content,
  DivisionButton,
  DivisionChangeName,
  HeaderContainer,
  HeaderMobileContainer,
  IconLoading,
  Left,
  PageContainer,
  PageTitle,
  ValeIcon,
  ModalErrorMessage,
  FormField,
  StyledInput,
  FieldError,
  RecaptchaWrapper
} from './styles';

export default function GasVoucherValidation() {
  const captchaSitekey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const captchaRef = useRef(null);

  const history = useHistory();
  const [codeChanged, setCodeChanged] = useState('');
  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [loadingSave, setLoadingSave] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenReCaptcha, setTokenReCaptcha] = useState(null);

  const stores = useSelector((state) => state.economicGroupReducer.stores);

  async function handleCodeValidation() {
    setLoadingSave(true);

    const token = captchaRef.current.getValue();
    setTokenReCaptcha(token);

    const hasRequiredBase = codeChanged && token;
    const hasCpfPhone = requireCpfPhoneValegas ? cpf && phone : true;

    if (hasRequiredBase && hasCpfPhone) {
      if (requireCpfPhoneValegas) {
        if (!isValidCPF(cpf)) {
          toast.error('CPF inválido');
          setLoadingSave(false);
          return;
        }
        if (!isValidPhone(phone)) {
          toast.error('Telefone inválido');
          setLoadingSave(false);
          return;
        }
      }

      const headers = {
        "Content-Type": "application/json",
        access_token: localStorage.getItem('access_token'),
      };

      const url = `/residential/store/v1/voucher/user-me/validate`;
      const storeId = localStorage.getItem("storeId");

      const body = {
        voucherCode: codeChanged,
        storeId,
        captchaToken: token,
      };

      if (requireCpfPhoneValegas) {
        body.consumerDocument = String(cpf).replace(/\D/g, '');
        body.consumerPhoneNumber = String(phone).replace(/\D/g, '');
      }

      api.post(url, body, { headers })
        .then(() => {
          setCodeChanged('');
          if (requireCpfPhoneValegas) {
            setCpf('');
            setPhone('');
          }
          toast.success("Vale validado com sucesso");
        })
        .catch((err) => {
          if (err.response?.status < 500) {
            setShowModal(true);
            setErrorMessage(err.response.data.message);
          } else {
            toast.error("Ocorreu um erro ao tentar validar o vale");
          }

          setTokenReCaptcha(null);
          captchaRef.current.reset();
        })
        .finally(() => {
          setLoadingSave(false);
        });
    } else {
      toast.error(requireCpfPhoneValegas ? "Todos os campos são obrigatórios" : "Código e captcha são obrigatórios");
      setLoadingSave(false);
    }
  }

  const handleRecaptchaChange = (value) => {
    setTokenReCaptcha(value);
  };

  const formatCPF = (value) => {
    const digits = String(value).replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.replace(/(\d{3})(\d+)/, '$1.$2');
    if (digits.length <= 9) return digits.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const handleCpfChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    if (cpfError) setCpfError('');
  };

  const handleCpfBlur = () => {
    if (!cpf) {
      setCpfError('');
      return;
    }
    if (!isValidCPF(cpf)) {
      setCpfError('CPF inválido');
    } else {
      setCpfError('');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    if (phoneError) setPhoneError('');
  };

  const handlePhoneBlur = () => {
    if (!phone) {
      setPhoneError('');
      return;
    }
    if (!isValidPhone(phone)) {
      setPhoneError('Telefone inválido');
    } else {
      setPhoneError('');
    }
  };

  const canValidate = requireCpfPhoneValegas
    ? tokenReCaptcha && codeChanged && cpf && phone && isValidCPF(cpf) && isValidPhone(phone)
    : tokenReCaptcha && codeChanged;

  return (
    <PageContainer>
      <SideBar menuSelected={"Minha Conta"}>
        <DivisionChangeName>
          <HeaderContainer>
            <HeaderDesktop stores={stores} />
          </HeaderContainer>

          <HeaderMobileContainer>
            <HeaderMobile />
          </HeaderMobileContainer>

          <Modal show={showModal} centered>
            <ModalErrorMessage>
              <WarningIcon style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }} />
              <span>{errorMessage}</span>
              <InfoButton
                onClick={() => {
                  setCodeChanged('');
                  setShowModal(false);
                }}
                style={{ width: '25%', marginTop: '1rem' }}
              >
                Ok
              </InfoButton>
            </ModalErrorMessage>
          </Modal>

          <Content>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Left>
                <ValeIcon src={valegasicon} alt="Ícone do vale gás" />
                <PageTitle>Vale Gás</PageTitle>
              </Left>

              <BackIcon>
                <img
                  src={back}
                  alt="Voltar"
                  onClick={() => history.push('/valegas')}
                />
              </BackIcon>
            </div>

            <FormField>
              <label>
                <CheckCircleIcon style={{ marginRight: '0.3rem' }} />
                Validar Vale gás
              </label>
              <StyledInput
                maxLength={99}
                value={codeChanged}
                onChange={(e) => setCodeChanged(e.target.value)}
                placeholder="Código de autorização ou n° do vale"
              />
            </FormField>

            {requireCpfPhoneValegas && (
              <>
                <FormField>
                  <label>
                    <PiIdentificationCardFill style={{ marginRight: '0.3rem' }} />
                    CPF
                  </label>
                  <StyledInput
                    maxLength={14}
                    value={cpf}
                    onChange={handleCpfChange}
                    onBlur={handleCpfBlur}
                    placeholder="N° do CPF do cliente"
                  />
                  {cpfError && <FieldError>{cpfError}</FieldError>}
                </FormField>

                <FormField>
                  <label>
                    <FaSquarePhone style={{ marginRight: '0.3rem' }} />
                    Celular
                  </label>
                  <StyledInput
                    maxLength={15}
                    value={phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder="N° de celular do cliente"
                  />
                  {phoneError && <FieldError>{phoneError}</FieldError>}
                </FormField>
              </>
            )}

            {/* Captcha */}
            <RecaptchaWrapper>
              <ReCaptcha
                sitekey={captchaSitekey}
                ref={captchaRef}
                onChange={handleRecaptchaChange}
              />
            </RecaptchaWrapper>

            <DivisionButton>
              {!canValidate ? (
                <InfoButtonDisabled>
                  {loadingSave ? (
                    <IconLoading className="fa fa-spinner fa-pulse fa-fw" />
                  ) : "Validar"}
                </InfoButtonDisabled>
              ) : (
                <InfoButton onClick={handleCodeValidation}>
                  {loadingSave ? (
                    <IconLoading className="fa fa-spinner fa-pulse fa-fw" />
                  ) : "Validar"}
                </InfoButton>
              )}
            </DivisionButton>

          </Content>
        </DivisionChangeName>

        <OpenChatBot />
      </SideBar>
    </PageContainer>
  );
}
